import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Label,
  Row,
} from "reactstrap";
import { useLazyQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { useAuth } from "components/custom/authprovider";
import sendMutationContext from "utils/sendContext";
import { GET_ADMIN_PROCESSOR_STATUS_REPORT } from "graphql/admin/queries/transactions";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import AdminNav from "./AdminNav";
import "../../assets/css/admin.css";
import "../../assets/css/processor-status-report.css";

const formatAmount = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

function ProcessorStatusReport() {
  const { wt } = useAuth();
  const history = useHistory();
  const [logoutFlag, setLogoutFlag] = useState(false);
  const [error, setError] = useState("");
  const requestVersion = useRef(0);
  const [getProcessorStatusReport, { loading }] = useLazyQuery(
    GET_ADMIN_PROCESSOR_STATUS_REPORT,
    { ...sendMutationContext(wt, setLogoutFlag), fetchPolicy: "no-cache" },
  );

  useEffect(() => {
    if (logoutFlag) {
      history.push("/admin/logout");
    }
  }, [logoutFlag, history]);

  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const [report, setReport] = useState(null);
  const datesSelected =
    startDateTime instanceof Date && endDateTime instanceof Date;
  const validRange = datesSelected && startDateTime <= endDateTime;
  const [expanded, setExpanded] = useState([]);
  const allExpanded = report && expanded.length === report.processors.length;

  const toggleProcessor = (name) =>
    setExpanded((current) =>
      current.includes(name)
        ? current.filter((item) => item !== name)
        : [...current, name],
    );

  const changeDate = (setDate) => (value) => {
    setDate(typeof value === "string" ? value : value.toDate());
    setReport(null);
    setError("");
    requestVersion.current += 1;
  };

  const runReport = async (event) => {
    event.preventDefault();
    if (!validRange || loading) {
      return;
    }

    const version = ++requestVersion.current;
    const start = startDateTime;
    const end = endDateTime;
    setReport(null);
    setError("");
    try {
      const result = await getProcessorStatusReport({
        variables: {
          startDateTime: start.toISOString(),
          endDateTime: end.toISOString(),
        },
      });
      if (version !== requestVersion.current) {
        return;
      }
      const data = result.data?.adminGetProcessorStatusReport;
      if (!data?.status) {
        setError(
          data?.error || "Unable to generate the processor status report.",
        );
        return;
      }
      setReport({ processors: data.processors, start, end });
      setExpanded(data.processors.length ? [data.processors[0].paymentid] : []);
    } catch {
      if (version === requestVersion.current) {
        setError(
          "Unable to generate the processor status report. Please try again.",
        );
      }
    }
  };

  return (
    <div className="admin-wrapper processor-status-page">
      <AdminNav />
      <Container fluid className="content" tag="main">
        <Row>
          <Col md="12">
            <Card className="card-dark">
              <CardHeader className="card-header-dark">
                <Row>
                  <Col md="12">Processor Status Report</Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Form onSubmit={runReport}>
                  <Row className="align-items-end">
                    <Col md="5">
                      <FormGroup>
                        <Label for="processor-start">Start Date Time</Label>
                        <Datetime
                          value={startDateTime}
                          onChange={changeDate(setStartDateTime)}
                          dateFormat="MM/DD/YYYY"
                          timeFormat="h:mm A"
                          inputProps={{
                            id: "processor-start",
                            required: true,
                            placeholder: "Select start datetime",
                            autoComplete: "off",
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="5">
                      <FormGroup>
                        <Label for="processor-end">End Date Time</Label>
                        <Datetime
                          value={endDateTime}
                          onChange={changeDate(setEndDateTime)}
                          dateFormat="MM/DD/YYYY"
                          timeFormat="h:mm A"
                          isValidDate={(date) =>
                            !(startDateTime instanceof Date) ||
                            date.isSameOrAfter(startDateTime, "day")
                          }
                          inputProps={{
                            id: "processor-end",
                            required: true,
                            placeholder: "Select end datetime",
                            autoComplete: "off",
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="2">
                      <FormGroup>
                        <Button
                          type="submit"
                          color="success"
                          disabled={!validRange || loading}
                        >
                          {loading ? "Running…" : "Run Report"}
                        </Button>
                      </FormGroup>
                    </Col>
                  </Row>
                  {datesSelected && !validRange && (
                    <p role="alert">
                      End Date Time must be on or after Start Date Time.
                    </p>
                  )}
                </Form>
                {loading && <p role="status">Generating report...</p>}
                {error && <p role="alert">{error}</p>}
                {report && (
                  <div aria-live="polite">
                    <p>
                      Report period: {new Date(report.start).toLocaleString()} –{" "}
                      {new Date(report.end).toLocaleString()}
                    </p>
                    {report.processors.length === 0 ? (
                      <p>No transactions found for the selected date range.</p>
                    ) : (
                      <section
                        className="customer-card processor-status-panel"
                        aria-labelledby="processor-details-title"
                      >
                        <div className="processor-status-heading">
                          <h2 id="processor-details-title">
                            Processor details
                          </h2>
                          <button
                            type="button"
                            className="processor-status-expand"
                            onClick={() =>
                              setExpanded(
                                allExpanded
                                  ? []
                                  : report.processors.map(
                                      ({ paymentid }) => paymentid,
                                    ),
                              )
                            }
                          >
                            {allExpanded ? "Collapse all" : "Expand all"}
                          </button>
                        </div>
                        <div className="processor-status-list">
                          {report.processors.map(
                            ({ paymentid, name, referrals }) => {
                              const isExpanded = expanded.includes(paymentid);
                              const count = referrals.reduce(
                                (sum, referral) => sum + referral.count,
                                0,
                              );
                              const total = referrals.reduce(
                                (sum, referral) => sum + referral.total,
                                0,
                              );
                              return (
                                <div
                                  className="processor-status-item"
                                  key={paymentid}
                                >
                                  <h3>
                                    <button
                                      type="button"
                                      className="processor-status-row"
                                      id={`processor-${paymentid}`}
                                      aria-expanded={isExpanded}
                                      aria-controls={`details-${paymentid}`}
                                      onClick={() => toggleProcessor(paymentid)}
                                    >
                                      <span
                                        className={`processor-status-chevron${isExpanded ? " is-expanded" : ""}`}
                                        aria-hidden="true"
                                      />
                                      <span className="processor-status-name">
                                        {name}
                                      </span>
                                      <span className="processor-status-count">
                                        {count} transactions
                                      </span>
                                      <span className="processor-status-total">
                                        {formatAmount(total)}
                                      </span>
                                    </button>
                                  </h3>
                                  <div
                                    id={`details-${paymentid}`}
                                    role="region"
                                    aria-labelledby={`processor-${paymentid}`}
                                    hidden={!isExpanded}
                                    className="processor-status-details"
                                  >
                                    <table>
                                      <caption className="sr-only">
                                        {name} referral details
                                      </caption>
                                      <thead>
                                        <tr>
                                          <th scope="col">Referral Code</th>
                                          <th scope="col">Count</th>
                                          <th scope="col">Total</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {referrals.map((referral) => (
                                          <tr key={referral.code}>
                                            <td>
                                              {referral.code || "No referral"}
                                            </td>
                                            <td>{referral.count}</td>
                                            <td>
                                              {formatAmount(referral.total)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      </section>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default ProcessorStatusReport;
